import { DetectionZone, Vector2 } from "../models";

/**
 * Corners a zone needs before it is a shape at all. The drawing hint promises this number, the save
 * path honours it, and the server refuses anything under it (Shared/Helpers/ZoneGeometry.cs) — they
 * are the same rule and must stay the same number.
 */
export const MINIMUM_ZONE_VERTICES = 3;

/** A zone outline stores its closing vertex by repeating the first one. */
const isClosedRing = (points?: Vector2[]) => {
    const numPoints = points?.length ?? 0;
    return numPoints > 1 && points![0].x === points![numPoints - 1].x && points![0].y === points![numPoints - 1].y;
};

declare module "../models/DetectionZone.gen" {
    interface DetectionZone {
        clampPointIfClosing(point: Vector2, threshold: number): Vector2;
        isComplete(): boolean;
        vertexCount(): number;
        canFormPolygon(): boolean;
        closedRing(): DetectionZone;
    }
}

DetectionZone.prototype.isComplete = function () {
    const numPoints = this.points?.length ?? 0;
    return (numPoints > 2 && this.points![0].x === this.points![numPoints - 1].x && this.points![0].y === this.points![numPoints - 1].y);
}

/** Corners, not points: a closed outline's repeated last vertex is the first one again. */
DetectionZone.prototype.vertexCount = function () {
    const numPoints = this.points?.length ?? 0;
    return isClosedRing(this.points) ? numPoints - 1 : numPoints;
}

DetectionZone.prototype.canFormPolygon = function () {
    return this.vertexCount() >= MINIMUM_ZONE_VERTICES;
}

/**
 * The zone with its outline closed, which is how a polygon is stored everywhere downstream. A zone
 * that has the corners the drawing hint asked for is finishable, so it is finished here rather than
 * dropped on the way to a save — the user drew the shape, the repeated vertex is bookkeeping.
 */
DetectionZone.prototype.closedRing = function () {
    if (!this.points?.length || isClosedRing(this.points)) { return this; }
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this, { points: [...this.points, this.points[0]] }) as DetectionZone;
}

DetectionZone.prototype.clampPointIfClosing = function (point: Vector2, threshold: number) {
    var isAPolygon = (this.points?.length ?? 0) > 2;
    if (!isAPolygon) { return point; }

    var isCloseToStartingPoint = this.points![0].getDistanceTo(point) < threshold;
    if (!isCloseToStartingPoint) { return point; }

    return this.points![0];
}
